import { useEffect, useState, useCallback } from "react";
import { Building2, Plus, Pencil, Users2, Loader2, Copy, Check, ShieldCheck, Power } from "lucide-react";
import { toast } from "sonner";
import { AppLayout } from "@/components/AppLayout";
import { api, formatApiErrorDetail, formatWIB } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const emptyBranch = {
  name: "", code: "", address: "", city: "", province: "",
  whatsapp: "", email: "", head_name: "", is_active: true,
};

function maskId(id) {
  if (!id) return "-";
  return id.length > 12 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id;
}

export default function Branches() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null | {branch...}
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [usersFor, setUsersFor] = useState(null); // branch object
  const [branchUsers, setBranchUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userForm, setUserForm] = useState(null); // null | {name,email,role}
  const [creatingUser, setCreatingUser] = useState(false);
  const [tempCred, setTempCred] = useState(null); // {email, temp_password}
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/branches");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setForm({ ...emptyBranch }); };
  const openEdit = (b) => {
    setEditId(b.id);
    setForm({
      name: b.name || "", code: b.code || "", address: b.address || "",
      city: b.city || "", province: b.province || "", whatsapp: b.whatsapp || "",
      email: b.email || "", head_name: b.head_name || "", is_active: b.is_active !== false,
    });
  };

  const saveBranch = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) await api.put(`/branches/${editId}`, form);
      else await api.post("/branches", form);
      toast.success(editId ? "Cabang diperbarui." : "Cabang ditambahkan.");
      setForm(null); setEditId(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (b) => {
    try {
      await api.patch(`/branches/${b.id}/active`, { is_active: !(b.is_active !== false) });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openUsers = async (b) => {
    setUsersFor(b); setBranchUsers([]); setLoadingUsers(true);
    try {
      const { data } = await api.get(`/branches/${b.id}/users`);
      setBranchUsers(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoadingUsers(false);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setCreatingUser(true);
    try {
      const { data } = await api.post(`/branches/${usersFor.id}/users`, userForm);
      setTempCred({ email: data.email, temp_password: data.temp_password });
      setUserForm(null);
      const { data: ud } = await api.get(`/branches/${usersFor.id}/users`);
      setBranchUsers(ud.items || []);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreatingUser(false);
    }
  };

  const copyCred = () => {
    navigator.clipboard?.writeText(`${tempCred.email} / ${tempCred.temp_password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="branches-page">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-[#0f2847] flex items-center gap-2">
              <Building2 className="h-6 w-6" /> Kelola Cabang
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Manajemen cabang multi-lokasi. Setiap cabang memiliki data yang terpisah penuh.
            </p>
          </div>
          <Button onClick={openCreate} data-testid="branch-add-button" className="bg-[#0f2847] hover:bg-[#0b1e38]">
            <Plus className="h-4 w-4 mr-1.5" /> Tambah Cabang
          </Button>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="branches-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Cabang</th>
                  <th className="text-left px-4 py-3 font-semibold">Kode</th>
                  <th className="text-left px-4 py-3 font-semibold">Branch ID</th>
                  <th className="text-left px-4 py-3 font-semibold">Lokasi</th>
                  <th className="text-left px-4 py-3 font-semibold">Akun</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-right px-4 py-3 font-semibold">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                    <Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Belum ada cabang.</td></tr>
                ) : items.map((b) => (
                  <tr key={b.id} data-testid={`branch-row-${b.code}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{b.name}</div>
                      {b.head_name && <div className="text-xs text-slate-400">Kepala: {b.head_name}</div>}
                      <div className="text-[11px] text-slate-400">Dibuat {formatWIB(b.created_at)}</div>
                    </td>
                    <td className="px-4 py-3"><span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">{b.code}</span></td>
                    <td className="px-4 py-3"><span className="font-mono text-[11px] text-slate-500" title={b.id}>{maskId(b.id)}</span></td>
                    <td className="px-4 py-3 text-slate-600">{[b.city, b.province].filter(Boolean).join(", ") || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{b.user_count ?? 0}</td>
                    <td className="px-4 py-3">
                      {b.is_active !== false
                        ? <Badge className="bg-green-50 text-green-700 border border-green-200">Aktif</Badge>
                        : <Badge className="bg-slate-100 text-slate-500 border border-slate-200">Nonaktif</Badge>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => openUsers(b)} data-testid={`branch-users-${b.code}`}>
                          <Users2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openEdit(b)} data-testid={`branch-edit-${b.code}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => toggleActive(b)} data-testid={`branch-toggle-${b.code}`}
                          className={b.is_active !== false ? "text-amber-600" : "text-green-600"}>
                          <Power className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create / Edit branch */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-lg" data-testid="branch-form-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Cabang" : "Tambah Cabang"}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={saveBranch} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Nama Cabang *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required data-testid="branch-name-input" placeholder="IEC Cabang ..." />
                </div>
                <div>
                  <Label>Kode Cabang *</Label>
                  <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    required data-testid="branch-code-input" placeholder="IEC-XXX" />
                </div>
                <div>
                  <Label>WhatsApp</Label>
                  <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                    data-testid="branch-whatsapp-input" placeholder="08xxxx" />
                </div>
                <div>
                  <Label>Kota</Label>
                  <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="branch-city-input" />
                </div>
                <div>
                  <Label>Provinsi</Label>
                  <Input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} data-testid="branch-province-input" />
                </div>
                <div className="col-span-2">
                  <Label>Alamat</Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="branch-address-input" />
                </div>
                <div>
                  <Label>Email (opsional)</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="branch-email-input" />
                </div>
                <div>
                  <Label>Nama Kepala (opsional)</Label>
                  <Input value={form.head_name} onChange={(e) => setForm({ ...form, head_name: e.target.value })} data-testid="branch-head-input" />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setForm(null)}>Batal</Button>
                <Button type="submit" disabled={saving} className="bg-[#0f2847] hover:bg-[#0b1e38]" data-testid="branch-save-button">
                  {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Simpan
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Branch users */}
      <Dialog open={!!usersFor} onOpenChange={(o) => !o && setUsersFor(null)}>
        <DialogContent className="max-w-lg" data-testid="branch-users-dialog">
          <DialogHeader><DialogTitle>Akun — {usersFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {loadingUsers ? (
              <div className="py-6 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
            ) : branchUsers.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">Belum ada akun untuk cabang ini.</p>
            ) : (
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {branchUsers.map((u) => (
                  <li key={u.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div>
                      <div className="font-medium text-slate-800">{u.name}</div>
                      <div className="text-xs text-slate-500">{u.email}</div>
                    </div>
                    <Badge className="bg-slate-100 text-slate-600 border border-slate-200">
                      {u.role === "kepala_cabang" ? "Kepala Cabang" : "Admin Cabang"}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
            <Button variant="outline" className="w-full" onClick={() => setUserForm({ name: "", email: "", role: "admin_cabang" })}
              data-testid="branch-adduser-button">
              <Plus className="h-4 w-4 mr-1.5" /> Tambah Akun Cabang
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create branch user */}
      <Dialog open={!!userForm} onOpenChange={(o) => !o && setUserForm(null)}>
        <DialogContent className="max-w-md" data-testid="branch-createuser-dialog">
          <DialogHeader><DialogTitle>Tambah Akun — {usersFor?.name}</DialogTitle></DialogHeader>
          {userForm && (
            <form onSubmit={createUser} className="space-y-3">
              <div>
                <Label>Nama Lengkap *</Label>
                <Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  required data-testid="createuser-name-input" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  required data-testid="createuser-email-input" />
              </div>
              <div>
                <Label>Peran *</Label>
                <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                  <SelectTrigger data-testid="createuser-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kepala_cabang">Kepala Cabang</SelectItem>
                    <SelectItem value="admin_cabang">Admin Cabang</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-slate-500">Kata sandi sementara dibuat otomatis & aman, ditampilkan sekali saja setelah akun dibuat.</p>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setUserForm(null)}>Batal</Button>
                <Button type="submit" disabled={creatingUser} className="bg-[#0f2847] hover:bg-[#0b1e38]" data-testid="createuser-submit-button">
                  {creatingUser && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Buat Akun
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* One-time temp password */}
      <Dialog open={!!tempCred} onOpenChange={(o) => !o && setTempCred(null)}>
        <DialogContent className="max-w-md" data-testid="tempcred-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-[#0f2847]">
            <ShieldCheck className="h-5 w-5" /> Kata Sandi Sementara</DialogTitle></DialogHeader>
          {tempCred && (
            <div className="space-y-3">
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800">
                Salin sekarang. Kata sandi ini <b>tidak akan ditampilkan lagi</b>. Akun wajib mengganti sandi saat login pertama.
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 font-mono text-sm break-all">
                <div className="text-slate-500 text-xs mb-1">Email</div>
                <div className="mb-2" data-testid="tempcred-email">{tempCred.email}</div>
                <div className="text-slate-500 text-xs mb-1">Kata Sandi Sementara</div>
                <div data-testid="tempcred-password">{tempCred.temp_password}</div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={copyCred} data-testid="tempcred-copy-button">
                  {copied ? <Check className="h-4 w-4 mr-1.5 text-green-600" /> : <Copy className="h-4 w-4 mr-1.5" />}
                  {copied ? "Tersalin" : "Salin"}
                </Button>
                <Button className="flex-1 bg-[#0f2847] hover:bg-[#0b1e38]" onClick={() => setTempCred(null)} data-testid="tempcred-close-button">
                  Selesai
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
